import { Divider, Flex } from "@chakra-ui/react";
import { LocalDate, LocalDateTime, LocalTime } from "@js-joda/core";
import immer from "immer";
import SingleDatePicker from "../../shared/components/DatePicker/SingleDatePicker";
import { AddButton } from "./AddButton";
import { PatientTaskCustomizeDayTimeParamsTypes } from "./PatientTaskCustomizeDayTimeParams.types";
import { TimeTangePicker } from "./TimeSlotTimeRagePicker";
import { TrashButton } from "./TrashButton";

export function PatientTaskCustomizeDayTimeParamsSpecificDates(props: {
  dateRange: PatientTaskCustomizeDayTimeParamsTypes.DateRange;
  dayTimeScheduleOverrideParams: PatientTaskCustomizeDayTimeParamsTypes.DayTimeParamsType.SpecificDates;
  onChange: (
    params: PatientTaskCustomizeDayTimeParamsTypes.DayTimeParamsType.SpecificDates
  ) => void;
}) {
  const handleUpdateTimeSlotTime = (
    date: LocalDate,
    timeSlotIndex: number,
    startTime: LocalTime,
    endTime: LocalTime
  ) => {
    const newDayTimeParams = immer(props.dayTimeScheduleOverrideParams, (draft) => {
      draft.params.forEach((param) => {
        if (param.date.equals(date)) {
          param.timeSlots[timeSlotIndex] = { startTime, endTime };
        }
      });
    });
    props.onChange(newDayTimeParams);
  };

  const handleUpdateTimeSlotDate = (oldDate: LocalDate, newDate: LocalDate) => {
    const newDayTimeParams = immer(props.dayTimeScheduleOverrideParams, (draft) => {
      draft.params.forEach((param) => {
        if (param.date.equals(oldDate)) {
          param.date = newDate;
        }
      });
    });
    props.onChange(newDayTimeParams);
  };

  const handleAddTimeSlot = (date: LocalDate) => {
    const newDayTimeParams = immer(props.dayTimeScheduleOverrideParams, (draft) => {
      const foundDate = draft.params.find((param) => param.date.equals(date));
      if (foundDate !== undefined) {
        foundDate.timeSlots.push({ startTime: LocalTime.of(9), endTime: LocalTime.of(13) });
      }
    });
    props.onChange(newDayTimeParams);
  };

  const handleRemoveTimeSlot = (date: LocalDate, timeSlotIndex: number) => {
    const newDayTimeParams = immer(props.dayTimeScheduleOverrideParams, (draft) => {
      const foundDate = draft.params.find((param) => param.date.equals(date));
      if (foundDate !== undefined) {
        foundDate.timeSlots.splice(timeSlotIndex, 1);
        if (foundDate.timeSlots.length === 0) {
          draft.params.splice(draft.params.indexOf(foundDate), 1);
        }
      }
    });
    props.onChange(newDayTimeParams);
  };

  const handleRemoveDate = (date: LocalDate) => {
    const newDayTimeParams = immer(props.dayTimeScheduleOverrideParams, (draft) => {
      const foundDateIndex = draft.params.findIndex((param) => param.date.equals(date));
      if (foundDateIndex > -1) {
        draft.params.splice(foundDateIndex, 1);
      }
    });
    props.onChange(newDayTimeParams);
  };

  const handleAddDate = (date: LocalDate | null) => {
    if (
      date === null ||
      props.dayTimeScheduleOverrideParams.params.some((param) => param.date.equals(date))
    ) {
      return;
    }
    const newDayTimeParams = immer(props.dayTimeScheduleOverrideParams, (draft) => {
      draft.params.push({
        date,
        timeSlots: [{ startTime: LocalTime.of(9), endTime: LocalTime.of(13) }],
      });
    });
    props.onChange(newDayTimeParams);
  };

  const minDate = props.dateRange[0].atStartOfDay();
  const maxDate = props.dateRange[1]?.atStartOfDay() ?? LocalDateTime.now();
  const excludeDates = props.dayTimeScheduleOverrideParams.params.map((param) =>
    param.date.atStartOfDay()
  );

  return (
    <Flex flexDirection="column" gap={4}>
      <Flex direction="column" gap={4} w="full">
        {props.dayTimeScheduleOverrideParams.params.map(({ date, timeSlots }) => {
          return (
            <Flex key={date.toString()} direction="column" gap={2} w="full">
              <Flex direction="row">
                <SingleDatePicker
                  excludeDates={excludeDates.filter((x) => !x.toLocalDate().isEqual(date))}
                  isClearable={false}
                  maxDate={maxDate}
                  minDate={minDate}
                  selected={date}
                  onChange={(newDate) =>
                    newDate !== null && handleUpdateTimeSlotDate(date, newDate)
                  }
                />
                <TrashButton onClick={() => handleRemoveDate(date)} />
              </Flex>
              {timeSlots.map((timeSlots, index) => (
                <TimeTangePicker
                  key={`${date.toString()}${timeSlots.startTime.toString()}${timeSlots.endTime.toString()}${index}`}
                  timeSlot={timeSlots}
                  onRemoveTimeSlot={() => handleRemoveTimeSlot(date, index)}
                  onUpdateTimeSlot={(startTime, endTime) =>
                    handleUpdateTimeSlotTime(date, index, startTime, endTime)
                  }
                />
              ))}
              <AddButton onClick={() => handleAddTimeSlot(date)}>Add Time Slot</AddButton>
              <Divider />
            </Flex>
          );
        })}
        <SingleDatePicker
          excludeDates={excludeDates}
          inputProps={{
            fontWeight: "bold",
            cursor: "pointer",
            textAlign: "center",
          }}
          isClearable={false}
          maxDate={maxDate}
          minDate={minDate}
          placeholderText="Click To Add Date"
          selected={null}
          onChange={handleAddDate}
        />
      </Flex>
    </Flex>
  );
}
